import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ConvertAvailabilityRequestDialog from "../../components/convertAvailabilityRequestDialog/ConvertAvailabilityRequestDialog";

import { withState } from "../../../services/state";

import {
  getMembers as apiGetMembers,
  getAvailabilityRequest as apiGetAvailabilityRequest,
  addPlayerToAvailabilityRequest as apiAddPlayerToAvailabilityRequest,
  removePlayerFromAvailabilityRequest as apiRemovePlayerFromAvailabilityRequest,
  convertAvailabilityRequest as apiConvertAvailabilityRequest,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import DialogModal from "../../components/dialogModal/DialogModal";
import Section from "../../components/section/Section";
import Label from "../../components/label/Label";
import Button from "../../components/button/Button";
import SingleLineTextInput from "../../components/singleLineTextInput/SingleLineTextInput";
import Conversation from "../../components/conversation/Conversation";
import ChatButton from "../../components/chatButton/ChatButton";
import AvailabilityResponseDialog from "../../components/availabilityResponseDialog/AvailabilityResponseDialog";

import AvailabilityRequestPlayersTable from "../../tables/availabilityRequestPlayersTable/AvailabilityRequestPlayersTable";
import ToggleAvailabilityRequestPlayersForm from "../../forms/toggleAvailabilityRequestPlayersForm/ToggleAvailabilityRequestPlayersForm";

const Detail = styled("div")({
  marginBottom: 20,
});

const ChoosePlayerButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
  "& > div": {
    margin: "0 5px",
  },
  [theme.breakpoints.down("sm")]: {
    display: "block",
    "& > div": {
      marginBottom: "10px",
    },
  },
}));

function AvailabilityRequestPage({ classes, dispatch, context }) {
  const { availabilityRequestId } = useParams();
  const navigate = useNavigate();

  const [showConvertAvailabilityRequest, setConvertAvailabilityRequest] =
    React.useState(null);
  const [convertingAvailabilityRequest, setConvertingAvailabilityRequest] =
    React.useState(false);
  const [convertAvailabilityRequestError, setConvertAvailabilityRequestError] =
    React.useState("");
  const [showAddPlayer, toggleShowAddPlayer] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [gettingMembers, setGettingMembers] = React.useState(true);
  const [members, setMembers] = React.useState([]);
  const [availabilityRequest, setAvailabilityRequest] = React.useState(true);
  // const [addingPlayerError, setAddPlayerError] = React.useState(true);
  const [addingPlayer, setAddingPlayer] = React.useState(true);
  // const [deletePlayerError, setDeletePlayerError] = React.useState(true);
  const [deletingPlayer, setDeletingPlayer] = React.useState(true);
  const [showAvailabilityRequestMessages, setShowAvailabilityRequestMessages] =
    React.useState(false);
  const [
    showEditAvailabilityRequestResponse,
    setShowEditAvailabilityRequestResponse,
  ] = React.useState(null);

  const getMembers = useCallback(async () => {
    try {
      if (!context.currentClub.clubId) {
        return false;
      }

      setGettingMembers(true);
      const data = await apiGetMembers(
        context.currentClub.clubId,
        context.currentPlayer.id
      );
      if (data && !data.reason) {
        setMembers(data);
      }
      setGettingMembers(false);
    } catch (e) {
      console.log(e);
      setGettingMembers(false);
    }
  }, [context.currentClub.clubId, context.currentPlayer.id]);

  const getAvailabilityRequest = useCallback(async () => {
    try {
      const data = await apiGetAvailabilityRequest(availabilityRequestId);
      if (data && !data.reason) {
        setAvailabilityRequest(data);
      }
    } catch (e) {
      console.log(e);
    }
  }, [availabilityRequestId]);

  const addPlayerToAvailabilityRequest = async (member) => {
    try {
      setAddingPlayer(member.playerId);
      await apiAddPlayerToAvailabilityRequest(
        availabilityRequestId,
        member.playerId
      );
      setAddingPlayer(false);
      // Dispatch custom event to notify sidebar to refresh player data
      window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
      getAvailabilityRequest();
    } catch (e) {
      setAddingPlayer(false);
      // setAddPlayerError(e.message);
    }
  };

  const removePlayerFromAvailabilityRequest = async (
    availabilityRequestPlayer
  ) => {
    try {
      setDeletingPlayer(availabilityRequestPlayer.player.id);
      await apiRemovePlayerFromAvailabilityRequest(
        availabilityRequestId,
        availabilityRequestPlayer.player.id
      );
      // Dispatch custom event to notify sidebar to refresh player data
      window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
      getAvailabilityRequest();
      setDeletingPlayer(false);
    } catch (e) {
      setDeletingPlayer(false);
      // setDeletePlayerError(e.message);
    }
  };

  const convertAvailabilityRequest = async (playerIds) => {
    try {
      setConvertingAvailabilityRequest(true);
      await apiConvertAvailabilityRequest(availabilityRequest.id, playerIds);
      setConvertingAvailabilityRequest(null);
      setConvertAvailabilityRequest(null);
      // Dispatch custom event to notify sidebar to refresh player data
      window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
      getAvailabilityRequest();
      navigate("/events/admin");
    } catch (e) {
      console.log(e);
      setConvertingAvailabilityRequest(false);
      setConvertAvailabilityRequestError(e.message);
    }
  };

  const isPlayerSelected = (member) => {
    return availabilityRequest.players.find(
      (availabilityRequestPlayer) =>
        availabilityRequestPlayer.player.id === member.playerId
    );
  };

  const reorderClubPlayers = () => {
    setMembers([]);
    setSearchTerm("");

    setTimeout(() => {
      setMembers(
        members.sort((a, b) => {
          let aSelected = isPlayerSelected(a);
          let bSelected = isPlayerSelected(b);

          if (aSelected && !bSelected) {
            return -1;
          } else if (bSelected && !aSelected) {
            return 1;
          } else {
            let aName = `${a.firstName} ${a.lastName}`;
            let bName = `${b.firstName} ${b.lastName}`;

            if (aName !== bName) {
              return bName > aName ? -1 : 1;
            } else {
              return b.id > a.id ? -1 : 1;
            }
          }
        })
      );
    }, 10);
  };

  const renderEventTime = () => {
    if (
      availabilityRequest.eventStartTime &&
      availabilityRequest.eventEndTime
    ) {
      return `${availabilityRequest.eventStartTime} - ${availabilityRequest.eventEndTime}`;
    } else if (availabilityRequest.eventStartTime) {
      return availabilityRequest.eventStartTime;
    } else if (availabilityRequest.eventEndTime) {
      return availabilityRequest.eventEndTime;
    }
  };

  useEffect(() => {
    getMembers();
    getAvailabilityRequest();
  }, [getMembers, getAvailabilityRequest]);

  return (
    <PageWrapper
      showHeader={true}
      showSidebar={true}
      title="Availability Request"
      showLoading={gettingMembers}
      actionButtons={
        <div style={{ display: "flex", alignItems: "center" }}>
          {(context.currentClub.isTreasurer ||
            availabilityRequest.isCaptainForAvailabilityRequestTeam) && (
            <Link to={`/availability-requests/${availabilityRequest.id}/edit`}>
              <IconButton color="default">
                <EditIcon />
              </IconButton>
            </Link>
          )}
          <ChatButton
            unreadCount={
              availabilityRequest.currentAvailabilityRequestPlayer?.unreadMessageCount || 0
            }
            changeEvent={() =>
              setAvailabilityRequest((prev) => ({
                ...prev,
                currentAvailabilityRequestPlayer: {
                  ...prev.currentAvailabilityRequestPlayer,
                  unreadMessageCount: 0,
                },
              }))
            }
            onOpenConversation={() => setShowAvailabilityRequestMessages(true)}
          />
        </div>
      }
    >
      <Section>
        {availabilityRequest &&
          availabilityRequest.type &&
          availabilityRequest.team && (
            <div>
              <Detail>
                <Label title="NAME" />
                <div>{availabilityRequest.name}</div>
              </Detail>
              <Detail>
                <Label title="EVENT DATE" />
                <div>
                  {format(
                    new Date(availabilityRequest.eventDate),
                    "do MMMM yyyy"
                  )}{" "}
                  {renderEventTime()}
                </div>
              </Detail>
              <Detail>
                <Label title="EVENT LOCATION" />
                <div>{availabilityRequest.eventLocation}</div>
              </Detail>
              <Detail>
                <Label title="EVENT TYPE" />
                <div>{availabilityRequest.type.name}</div>
              </Detail>
              <Detail>
                <Label title="TEAM" />
                <div>{availabilityRequest.team.name}</div>
              </Detail>

              {availabilityRequest.players && availabilityRequest.players.length > 0 && (
                <Detail>
                  <Label title="RESPONSES" />
                  <div>
                    <div>
                      {(() => {
                        const players = availabilityRequest.players.filter(p => !p.isAdministrator);
                        const yesCount = players.filter(p => p.respondedAt && p.isAvailable).length;
                        const noCount = players.filter(p => p.respondedAt && !p.isAvailable).length;
                        const pendingCount = players.filter(p => !p.respondedAt).length;
                        return `${yesCount} Yes, ${noCount} No, ${pendingCount} Pending`;
                      })()}
                    </div>
                    {!context.currentClub.isTreasurer &&
                      !availabilityRequest.isCaptainForAvailabilityRequestTeam &&
                      availabilityRequest.currentAvailabilityRequestPlayer && (
                        <Button
                          title={
                            availabilityRequest.currentAvailabilityRequestPlayer.respondedAt
                              ? "EDIT RESPONSE"
                              : "RESPOND"
                          }
                          onClick={() =>
                            setShowEditAvailabilityRequestResponse({
                              ...availabilityRequest.currentAvailabilityRequestPlayer,
                              availabilityRequestId: availabilityRequest.id,
                              player: context.currentPlayer,
                            })
                          }
                          style={{ marginTop: '20px' }}
                        />
                      )}
                  </div>
                </Detail>
              )}
              {(context.currentClub.isTreasurer ||
                availabilityRequest.isCaptainForAvailabilityRequestTeam) &&
                availabilityRequest.haveAnyPlayersResponded && (
                  <div>
                    <Button
                      title="CONVERT"
                      onClick={() =>
                        setConvertAvailabilityRequest(availabilityRequest)
                      }
                    />
                  </div>
                )}
            </div>
          )}
      </Section>

      <Section>
        <AvailabilityRequestPlayersTable
          players={(availabilityRequest.players || []).sort((a, b) => {
            const aName = `${a.player?.firstName || ''} ${a.player?.lastName || ''}`.trim().toLowerCase();
            const bName = `${b.player?.firstName || ''} ${b.player?.lastName || ''}`.trim().toLowerCase();
            return aName.localeCompare(bName);
          })}
          additionalButtons={
            (context.currentClub.isTreasurer ||
              availabilityRequest.isCaptainForAvailabilityRequestTeam) && [
              {
                title: "Edit players",
                onPress: () => toggleShowAddPlayer(true),
              },
              {
                title: "Done",
                onPress: () => navigate(-1),
              },
            ]
          }
          rowActions={[
            ...(
              (context.currentClub.isTreasurer ||
                availabilityRequest.isCaptainForAvailabilityRequestTeam) && [
                {
                  type: "edit",
                  onClick: (availabilityRequestPlayer) =>
                    setShowEditAvailabilityRequestResponse({
                      ...availabilityRequestPlayer,
                      availabilityRequestId: availabilityRequest.id,
                    }),
                  tooltip: "Edit response",
                  hideButton: () => false,
                },
              ]
            ) || [],
            // Allow current player to edit their own response (only if not already treasurer/captain)
            ...(!context.currentClub.isTreasurer &&
              !availabilityRequest.isCaptainForAvailabilityRequestTeam
              ? [{
                  type: "edit",
                  onClick: (availabilityRequestPlayer) =>
                    setShowEditAvailabilityRequestResponse({
                      ...availabilityRequestPlayer,
                      availabilityRequestId: availabilityRequest.id,
                    }),
                  tooltip: "Edit your response",
                  hideButton: (availabilityRequestPlayer) =>
                    availabilityRequestPlayer.player.id !== context.currentPlayer.id,
                }]
              : []),
          ]}
          isTreasurerOrCaptain={
            context.currentClub.isTreasurer ||
            availabilityRequest.isCaptainForAvailabilityRequestTeam
          }
          myPlayer={context.currentPlayer}
        />
      </Section>

      {showAddPlayer && (
        <DialogModal
          title="Choose availability request players"
          onClose={() => toggleShowAddPlayer(false)}
          cancelText="Close"
        >
          <ChoosePlayerButtons>
            <SingleLineTextInput
              placeholder="Search"
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
            <div>
              <Button title="REORDER" onClick={() => reorderClubPlayers()} />
            </div>
          </ChoosePlayerButtons>
          <ToggleAvailabilityRequestPlayersForm
            members={members.filter(
              (member) =>
                `${member.firstName} ${member.lastName}`
                  .toLowerCase()
                  .indexOf(searchTerm.toLowerCase()) !== -1
            )}
            players={availabilityRequest.players}
            availabilityRequestPlayers={availabilityRequest.players || []}
            onAddPlayer={(membership) => {
              addPlayerToAvailabilityRequest(membership);
            }}
            addingPlayer={addingPlayer}
            onRemovePlayer={(availabilityRequestPlayer) => {
              removePlayerFromAvailabilityRequest(availabilityRequestPlayer);
            }}
            deletingPlayer={deletingPlayer}
            onClose={() => toggleShowAddPlayer(false)}
            filterPaidPlayers={true}
          />
        </DialogModal>
      )}

      {showAvailabilityRequestMessages && (
        <DialogModal
          title="Messages"
          onClose={() => {
            setShowAvailabilityRequestMessages(null);
            // Dispatch custom event to notify sidebar to refresh player data
            window.dispatchEvent(new CustomEvent('refreshPlayerNotifications'));
          }}
          cancelText="Close"
        >
          <Conversation
            context={context}
            availabilityRequestId={availabilityRequestId}
            isCaptainForTeam={
              context.currentClub.isTreasurer ||
              availabilityRequest.isCaptainForAvailabilityRequestTeam
            }
          />
        </DialogModal>
      )}

      {showEditAvailabilityRequestResponse && (
        <AvailabilityResponseDialog
          availabilityRequestPlayer={showEditAvailabilityRequestResponse}
          onClose={() => {
            getAvailabilityRequest();
            setShowEditAvailabilityRequestResponse(null);
          }}
          currentPlayerSelected={
            showEditAvailabilityRequestResponse.player.id ===
            context.currentPlayer.id
          }
          isTreasurerOrCaptain={
            context.currentClub.isTreasurer ||
            availabilityRequest.isCaptainForAvailabilityRequestTeam
          }
        />
      )}

      {showConvertAvailabilityRequest && (
        <ConvertAvailabilityRequestDialog
          availabilityRequest={availabilityRequest}
          onClose={() => setConvertAvailabilityRequest(false)}
          onConfirm={(playerIds) => convertAvailabilityRequest(playerIds)}
          submitting={convertingAvailabilityRequest}
          error={convertAvailabilityRequestError}
        />
      )}
    </PageWrapper>
  );
}

export default withState(AvailabilityRequestPage);
